import { graphql, useStaticQuery } from 'gatsby';
import * as React from 'react';
import styled, { css } from 'styled-components';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock-upgrade';
import { useWindowWidth } from '@react-hook/window-size';
import { useMediaQuery } from 'react-responsive';
import { cssClampValue, notEmpty } from '../../utils/common';
import MenuLink from '../MenuLink';
import SiteLogo from './SiteLogo';
import UserMenu from '../UserMenu';
import MenuDropdown from '../MenuDropdown';
import SearchBox from './SearchBox';
import zIndexes from '../../z-indexes';
import SearchContext from '../../contexts/SearchContext';
import CartButton from './CartButton/CartButton';

const Wrapper = styled.header<{ $hasMarginBottom: boolean }>`
  position: relative;
  padding: 30px 0;
  z-index: ${zIndexes.header};

  .electron-app & {
    padding-top: 40px;
    -webkit-app-region: drag;
  }

  .container {
    position: relative;
    align-items: center;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .electron-app & {
      & > * {
        -webkit-app-region: no-drag;
      }
    }
  }

  a {
    display: block;
    color: inherit;
    text-decoration: none;
  }

  @media (min-width: 778px) {
    margin-bottom: ${({ $hasMarginBottom }) =>
      $hasMarginBottom ? `${cssClampValue(20, 50)}` : '0'};
  }

  @media (min-width: 992px) {
    padding: 20px 0;
  }
`;

const SkipLink = styled.a`
  width: 1px;
  height: 1px;
  padding: 10px;
  background-color: var(--earth-fm--color--dark-beige);
  font-weight: 600;
  position: absolute;
  top: -10000px;
  left: -10000px;
  overflow: hidden;

  &:focus {
    width: auto;
    height: auto;
    outline: auto;
    outline-color: var(--earth-fm--color--blue);
    top: 0;
    left: 0;
    z-index: 100;
  }
`;

const SiteLogoWrapper = styled.div<{ $isSearchOpen: boolean }>`
  flex: 1;
  max-width: 110px;

  ${({ $isSearchOpen }) => $isSearchOpen && `display: none;`}

  @media (min-width: 992px) {
    display: block;
  }
`;

const MenuWrapper = styled.div<{ $isMenuOpen: boolean }>`
  position: fixed;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: -webkit-fill-available; /* mobile viewport bug fix */
  padding: 90px 0 30px;
  background: var(--earth-fm--color--background);
  clip-path: circle(0% at 100% 0);
  pointer-events: none;
  transition: clip-path 0.32s linear;

  ${({ $isMenuOpen }) =>
    $isMenuOpen
      ? css`
          clip-path: circle(100% at 50% 50%);
          pointer-events: initial;
        `
      : ''}

  @media (min-width: 992px) {
    position: static;
    flex-direction: row;
    align-items: center;
    width: auto;
    height: auto;
    min-height: auto;
    padding: 0;
    flex: 1;
    background: none;
    clip-path: none;
    pointer-events: auto;
    transition: none;
    z-index: auto;
  }
`;

const MenuContainer = styled.div`
  width: 100%;
  max-width: var(--earth-fm--container--max-width);
  margin-right: auto;
  margin-left: auto;
  padding-right: var(--earth-fm--container--padding);
  padding-left: var(--earth-fm--container--padding);
  overflow-y: auto;

  @media (min-width: 992px) {
    max-width: none;
    margin: 0;
    padding: 0;
    overflow-y: initial;
  }
`;

const Nav = styled.nav<{ $isMenuOpen: boolean }>`
  position: relative;
  display: ${({ $isMenuOpen }) => ($isMenuOpen ? 'flex' : 'none')};
  flex-direction: column;
  padding: 0;
  flex: 1;
  z-index: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  background:
    // Scroll shadow covers
    linear-gradient(var(--earth-fm--color--background) 30%, rgba(255, 255, 255, 0)),
    linear-gradient(rgba(255, 255, 255, 0), var(--earth-fm--color--background) 70%) 0 100%,
    // Scroll shadows
    radial-gradient(50% 0, farthest-side, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)),
    radial-gradient(50% 100%, farthest-side, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) 0 100%;
  background:
    // Scroll shadow covers
    linear-gradient(var(--earth-fm--color--background) 30%, rgba(255, 255, 255, 0)),
    linear-gradient(rgba(255, 255, 255, 0), var(--earth-fm--color--background) 70%) 0 100%,
    // Scroll shadows
    radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)),
    radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) 0 100%;
  background-repeat: no-repeat;
  background-color: var(--earth-fm--color--background);
  background-size:
    100% 40px,
    100% 40px,
    100% 14px,
    100% 14px;
  /* Opera doesn't support this in the shorthand */
  background-attachment: local, local, scroll, scroll;

  @media (min-width: 992px) {
    display: flex;
    flex-direction: row;
    gap: 50px;
    background: none;
    overflow: initial;
  }
`;

const MainMenu = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 20px;

  @media (min-width: 992px) {
    display: none;
    position: relative;
    min-height: 32px;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: ${cssClampValue(30, 40)};

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: -25px;
      width: 1px;
      height: 30px;
      background: var(--earth-fm--color--green);
      transform: translateY(-50%);
    }
  }

  @media (min-width: 1800px) {
    gap: 70px;
  }
`;

const MainMenuDesktop = styled(MainMenu)`
  display: none;

  @media (min-width: 992px) {
    display: flex;
  }
`;

const SearchWrapper = styled.div<{ $isSearchOpen: boolean; $isMenuOpen: boolean }>`
  margin: 0 ${({ $isSearchOpen }) => ($isSearchOpen ? '0' : cssClampValue(30, 50))} 0 auto;
  width: ${({ $isSearchOpen }) => ($isSearchOpen ? '100%' : 'auto')};

  // mobile version of search box is fully visible on tablet screens (see SearchBox component)
  // lets hide it when menu is open
  @media (min-width: 768px) {
    visibility: ${({ $isMenuOpen }) => ($isMenuOpen ? 'hidden' : 'visible')};
  }

  @media (min-width: 992px) {
    display: none;
    flex: 1;
    max-width: ${({ $isSearchOpen }) => ($isSearchOpen ? '100%' : '400px')};
    margin: 0 0 0 auto;
    padding-left: 50px;
    transition: max-width 0.3s ease-in;
  }
`;

const SearchWrapperDesktop = styled(SearchWrapper)`
  display: none;

  @media (min-width: 992px) {
    display: block;
  }
`;

const UserMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--earth-fm--color--green-50);

  @media (min-width: 992px) {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const CartWrapper = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--earth-fm--color--green-50);

  @media (min-width: 992px) {
    margin-top: 0;
    padding-top: 0;
    border-top: 0;
  }
`;

const ToggleButton = styled.button<{ $isMenuOpen: boolean; $isSearchOpen: boolean }>`
  ${({ $isSearchOpen }) => $isSearchOpen && `display: none;`}

  position: relative;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: none;
  order: 1;
  z-index: 2;

  span:first-of-type {
    position: absolute;
    top: 10px;
    left: 0;
    display: block;
    width: 12px;
    height: 2px;
    background: var(--earth-fm--color--green);

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: -7px;
      display: block;
      width: 19px;
      height: 2px;
      background: var(--earth-fm--color--green);
    }

    &::after {
      top: auto;
      bottom: -7px;
    }
  }

  ${({ $isMenuOpen }) =>
    $isMenuOpen
      ? css`
          position: fixed;
          top: 30px;
          right: calc(
            ((100vw - var(--earth-fm--container--max-width)) / 2) +
              var(--earth-fm--container--padding)
          );

          span:first-of-type {
            width: 0;
            height: 0;
            transform: translate(-4px, -1px);

            &::before,
            &::after {
              top: 0;
              width: 28.28px;
              transform-origin: center;
            }

            &::before {
              transform: rotate(-45deg);
            }

            &::after {
              transform: rotate(45deg);
            }
          }
        `
      : ''}

  @media (min-width: 992px) {
    display: none;
  }
`;

export default function Header({ hasMarginBottom = true }: { hasMarginBottom?: boolean }) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const mainMenuRef = React.useRef<HTMLUListElement>(null);

  const isDesktop = useMediaQuery({ minWidth: 992 });

  const { isSearchOpen, setIsSearchOpen } = React.useContext(SearchContext);

  const toggleMenu = React.useCallback(() => {
    setIsMenuOpen((open) => !open);
  }, []);

  const windowWidth = useWindowWidth();
  React.useEffect(() => {
    if (windowWidth >= 992) {
      setIsMenuOpen(false);
    }
  }, [windowWidth]);

  React.useEffect(() => {
    if (isDesktop) {
      if (wrapperRef.current) {
        enableBodyScroll(wrapperRef.current);
      }
      return undefined;
    }

    if (!wrapperRef.current) {
      clearAllBodyScrollLocks();
      return undefined;
    }

    if (isMenuOpen) {
      disableBodyScroll(wrapperRef.current, {
        /* Fix menu not scrollable bug in iOS */
        allowTouchMove: (el) => el === mainMenuRef.current,
      });
    } else {
      enableBodyScroll(wrapperRef.current);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isDesktop, isMenuOpen]);

  const data = useStaticQuery<Queries.HeaderQuery>(graphql`
    query Header {
      wpMenu(locations: { eq: PRIMARY_MENU }) {
        menuItems {
          nodes {
            id
            label
            uri
            target
            parentId
            childItems {
              nodes {
                id
                label
                uri
                target
              }
            }
          }
        }
      }
    }
  `);

  const desktopMenuItems = React.useMemo(
    () =>
      data.wpMenu?.menuItems?.nodes
        .filter(notEmpty)
        // filter out menu items with children, we are flattening the menu for desktop
        .filter((menuItem) => (menuItem.childItems?.nodes.length ?? []) === 0)
        .map((menuItem) => ({
          id: menuItem.id,
          to: menuItem.uri || '#',
          content: menuItem.label || '',
          target: menuItem.target || undefined,
        })) ?? [],
    [data.wpMenu?.menuItems?.nodes],
  );

  const handleKeyUp = React.useCallback(() => {
    setIsSearchOpen(false);
  }, [setIsSearchOpen]);

  return (
    <Wrapper ref={wrapperRef} $hasMarginBottom={hasMarginBottom}>
      <SkipLink href="#main-container" onKeyUp={handleKeyUp}>
        Skip to content
      </SkipLink>
      <div className="container">
        <SiteLogoWrapper $isSearchOpen={isSearchOpen}>
          <SiteLogo />
        </SiteLogoWrapper>
        <MenuWrapper $isMenuOpen={isMenuOpen}>
          <MenuContainer>
            <Nav $isMenuOpen={isMenuOpen}>
              <SearchWrapperDesktop $isSearchOpen={isSearchOpen} $isMenuOpen={isMenuOpen}>
                <SearchBox />
              </SearchWrapperDesktop>
              <UserMenuWrapper>
                <UserMenu />
              </UserMenuWrapper>
              <MainMenuDesktop>
                <li>
                  <MenuDropdown title="Menu" items={desktopMenuItems} align="end" />
                </li>
              </MainMenuDesktop>
              <MainMenu ref={mainMenuRef}>
                {data.wpMenu?.menuItems?.nodes
                  ? data.wpMenu?.menuItems?.nodes
                      .filter(notEmpty)
                      .filter((menuItem) => menuItem?.parentId === null)
                      .map((menuItem) => (
                        <li key={menuItem.id}>
                          {menuItem.childItems?.nodes && menuItem.childItems.nodes.length > 0 ? (
                            <MenuDropdown
                              title={menuItem.label}
                              items={menuItem.childItems.nodes
                                .filter(notEmpty)
                                .map((subMenuItem) => ({
                                  id: subMenuItem.id,
                                  to: subMenuItem.uri || '#',
                                  content: subMenuItem.label || '',
                                  target: subMenuItem.target || undefined,
                                }))}
                            />
                          ) : (
                            <MenuLink
                              to={menuItem.uri || '#'}
                              target={menuItem.target || undefined}
                            >
                              {menuItem.label}
                            </MenuLink>
                          )}
                        </li>
                      ))
                  : null}
              </MainMenu>
              <CartWrapper>
                <CartButton />
              </CartWrapper>
            </Nav>
          </MenuContainer>
        </MenuWrapper>
        <SearchWrapper $isSearchOpen={isSearchOpen} $isMenuOpen={isMenuOpen}>
          <SearchBox />
        </SearchWrapper>
        <ToggleButton
          type="button"
          $isMenuOpen={isMenuOpen}
          $isSearchOpen={isSearchOpen}
          onClick={toggleMenu}
        >
          <span />
          <span className="screen-reader-text">Mobile menu</span>
        </ToggleButton>
      </div>
    </Wrapper>
  );
}
